import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {merge, of} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap} from 'rxjs/operators';
import {HttpParams} from '@angular/common/http';
import {User} from '../../../dtos/user';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {UserService} from '../../../services/user/user.service';
import {MatDialog} from '@angular/material/dialog';
import {ManageUserComponent} from '../manage-user/manage-user.component';
import {NotificationService} from '../../../services/common/notification.service';
import {ConfirmationDialogComponent} from '../../common/confirmation-dialog/confirmation-dialog.component';
import {AuthService} from '../../../services/auth/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {BreakpointObserver, BreakpointState} from '@angular/cdk/layout';
import {UserSecurityEditDto} from "../../../dtos/user/user-security-edit-dto";
import {FormControl} from "@angular/forms";

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  standalone: false
})
export class UserManagementComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  columnList: string[] = ['name', 'firstName', 'email', 'role', 'actions', 'locked', 'delete'];
  displayedColumns: string[] = ['name', 'firstName', 'email', 'role', 'actions', 'locked', 'delete'];
  data: User[];
  resultsLength = 25;
  isLoadingResults = true;
  isRateLimitReached = false;
  inputString = new FormControl('');


  constructor(private notificationService: NotificationService,
              private userService: UserService, private router: Router, private route: ActivatedRoute,
              public authService: AuthService, private observer: BreakpointObserver,
              public matDialog: MatDialog) {
  }

  ngOnInit(): void {
    this.observer.observe([
      '(max-width:699px)',
      '(min-width:700px) and (max-width:850px)',
      '(min-width:851px)'
    ]).subscribe((state: BreakpointState) => {
      if (state.breakpoints['(max-width:699px)']) {
        this.displayedColumns = this.columnList.filter(s =>
          s !== 'name' && s !== 'firstName' && s !== 'actions' && s !== 'delete');
      }
      if (state.breakpoints['(min-width:700px) and (max-width:850px)']) {
        this.displayedColumns = this.columnList.filter(s => s !== 'name' && s !== 'firstName');
      }
      if (state.breakpoints['(min-width:851px)']) {
        this.displayedColumns = this.columnList;
      }
    });
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    this.searchUsers();
  }

  searchUsers() {
    merge()
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          let params = new HttpParams();
          params = params.append('substring', this.inputString?.value ?? '');
          this.paginator.pageIndex = 0;
          params = params.append('size', String(this.paginator.pageSize));
          params = params.append('page', String(this.paginator.pageIndex));
          if (String(this.sort.active) !== 'created' && this.sort.active !== undefined) {
            params = params.append('order', String(this.sort.direction));
            params = params.append('sort', String(this.sort.active));
          }
          return this.userService?.searchUsers(params)
            .pipe(catchError(() => of(null)));
        }),
        map(data => {
          // Flip flag to show that loading has finished.
          this.isLoadingResults = false;
          this.isRateLimitReached = data === null;

          if (data === null) {
            return [];
          }
          // Only refresh the result length if there is new data. In case of rate
          // limit errors, we do not want to reset the paginator to zero, as that
          // would prevent users from re-triggering requests.
          this.resultsLength = data.totalElements;
          return data.content;
        }), distinctUntilChanged(), debounceTime(1000)
      ).subscribe({
      next: (data) => {
        this.data = data;
      },
      error: error => {
        this.notificationService.openSnackBar(error);
      }
    });
  }

  deleteUser(user: User) {
    const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        headline: 'Nutzer wirklich löschen?',
        content: 'Mit dieser Aktion wird der Nutzer mit der E-Mail ' + user.email + ' und alle seine ' +
          'nutzerspezifischen Daten gelöscht. Wollen Sie trotzdem fortfahren?',
        action: 'Löschen'
      }
    });
    dialogRef.afterClosed().subscribe({
      next: (u) => {
        if (u) {
          this.userService.deleteUser(user.id).subscribe({
            next: () => {
              this.notificationService.openSnackBar(
                `Nutzer mit E-Mail ${user.email} wurde erfolgreich gelöscht.`
              );
              if (u.email === this.authService.getUser()) {
                this.authService.logoutUser();
                this.router.navigate(['/']);
              } else {
                this.searchUsers();
              }
            },
            error: (error) => {
              this.notificationService.openSnackBar(error);
            },
          });
        }
      },
      error: (error) => {
        this.notificationService.openSnackBar(error);
      },
    });

  }

  editUser(user: User) {
    const dialogRef = this.matDialog.open(ManageUserComponent, {
      data: {
        user
      }
    });
    dialogRef.afterClosed().subscribe({
      next: (u) => {
        if (u) {
          const securityData = new UserSecurityEditDto(user.id, user.isLocked, user.role);
          this.userService.updateUserSecurity(securityData).subscribe({
            next: () => {
              this.notificationService.openSnackBar(
                `Nutzer mit der E-Mail ${u.email} wurde die Rolle ${this.transformRoleName(u.role)} zugewiesen!`
              );
              this.searchUsers();
            },
            error: (error) => {
              this.notificationService.openSnackBar(error);
              this.searchUsers();
            },
          });
        }
      },
      error: (error) => {
        this.notificationService.openSnackBar(error);
      },
    });
  }

  toggleStatus(user: User) {
    const isLocked = !user.isLocked
    // FE isLocked status is used, because BE sends only false to avoid security data
    const action = isLocked ? 'gesperrt' : 'entsperrt';
    const securityData = new UserSecurityEditDto(user.id, !user.isLocked, user.role);
    this.userService.updateUserSecurity(securityData).subscribe({
      next: (u) => {
        user.isLocked = isLocked;
        this.notificationService.openSnackBar(
          `Nutzer mit der E-Mail ${u.email} wurde ${action}!`
        );
      },
      error: (error) => {
        this.notificationService.openSnackBar(error);
      },
    });
  }

  transformRoleName(role: string) {
    switch (role) {
      case 'ROLE_ADMIN':
        return 'Admin';
      case 'ROLE_USER_FREE':
        return 'Standard';
      case 'ROLE_USER_PAID':
        return 'Premium';
    }
  }

}
